import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectProjectId } from "reducers/client";

type ResponseTrackerSourceData = {
  name: string;
  responded: number;
  total: number;
  avgResponseDays: number | null;
};

type ResponseTrackerData = {
  responded: number;
  notResponded: number;
  cannotRespond: number;
  total: number;
  avgResponseDays: number | null;
  sources?: ResponseTrackerSourceData[] | null;
};

type ResponseTrackerCardProps = {
  data: ResponseTrackerData;
  href?: string | null;
  customName?: string;
  dateRange?: string;
  currentFilterString?: string;
};

const statusRowBase: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "10px 12px",
  borderRadius: "6px",
  marginBottom: "6px",
  cursor: "pointer",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  textDecoration: "none",
  color: "inherit",
};

const ResponseTrackerCard: React.FC<ResponseTrackerCardProps> = ({ data, customName, dateRange, currentFilterString }) => {
  // During filter changes the dashboard can briefly call this component with
  // a partial payload (e.g. an empty {} or missing counts). Coerce missing
  // numeric fields to 0 so the toLocaleString / arithmetic calls don't crash.
  data = {
    responded: data?.responded ?? 0,
    notResponded: data?.notResponded ?? 0,
    cannotRespond: data?.cannotRespond ?? 0,
    total: data?.total ?? 0,
    avgResponseDays: data?.avgResponseDays ?? null,
    sources: data?.sources ?? null,
  };

  const pct = (count: number) =>
    data.total > 0 ? ((count / data.total) * 100).toFixed(1) : "0.0";

  const navigate = useNavigate();
  const projectId = useSelector(selectProjectId);

  const buildFilterString = (responseFilter?: string): string => {
    // Start with existing filters, removing any existing responsestatus filter
    let filters = currentFilterString || "";
    if (filters === "reset") filters = "";

    const pairs = filters ? filters.split("||").filter(p => !p.startsWith("responsestatus::")) : [];

    // Add the response status filter if provided
    if (responseFilter) {
      pairs.push(`responsestatus::${responseFilter}`);
    }

    return pairs.join("||");
  };

  const goToReviewFeed = (responseFilter?: string) => {
    if (projectId) {
      const fs = buildFilterString(responseFilter);
      const query = fs ? `?fs=${encodeURIComponent(fs)}` : "";
      navigate(`/project/${projectId}/review-feed${query}`);
    }
  };

  const [hoveredRow, setHoveredRow] = React.useState<string | null>(null);

  const getRowStyle = (key: string, bgColor: string): React.CSSProperties => ({
    ...statusRowBase,
    backgroundColor: bgColor,
    transform: hoveredRow === key ? "scale(1.02)" : "scale(1)",
    boxShadow: hoveredRow === key ? "0 2px 8px rgba(0,0,0,0.08)" : "none",
  });

  const content = (
    <React.Fragment>
      <div className="SummaryCardHeader" style={{ backgroundColor: "#4a90d9" }}></div>
      <div
        className="SummaryCardBody"
        style={{
          padding: "16px 20px", flex: 1, display: "flex", alignItems: "center",
          cursor: "pointer", transition: "background-color 0.2s ease",
        }}
        onClick={() => goToReviewFeed()}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fc")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
        title="View all reviews"
      >
        <div className="SummaryCardSiteScore" style={{ textAlign: "center", width: "100%" }}>
          <h6 style={{ fontSize: "1.8em", fontWeight: 600, color: "#3a4550" }}>{customName || "Response Tracker"}</h6>
          {dateRange && (
            <p style={{ fontSize: "1.15em", color: "#92abbd", marginTop: "4px", marginBottom: "6px" }}>{dateRange}</p>
          )}
          <p className="Figure" style={{ fontSize: "3.2em", fontWeight: 700, color: "#2c3e50", margin: "12px 0 4px" }}>{data.total.toLocaleString("en-GB")}</p>
          <p style={{ fontSize: "1.2em", color: "#92abbd", letterSpacing: "0.03em" }}>total reviews</p>
        </div>
      </div>
      <div className="SummaryCardFooter" style={{ padding: "16px 20px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
          {/* Responded */}
          <div
            style={{
              ...getRowStyle("responded", "rgba(76, 175, 80, 0.06)"),
              flexDirection: "column",
              alignItems: "stretch",
              padding: "10px 12px",
            }}
            onMouseEnter={() => setHoveredRow("responded")}
            onMouseLeave={() => setHoveredRow(null)}
          >
            {/* Main responded row */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto auto",
                alignItems: "center",
                columnGap: "16px",
                cursor: "pointer",
              }}
              onClick={() => goToReviewFeed("Posted")}
              title="View responded reviews"
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "4px", height: "28px", borderRadius: "2px", backgroundColor: "#4caf50" }} />
                <div style={{ fontSize: "1.2em", fontWeight: 600, color: "#3a4550" }}>Responded</div>
              </div>
              <span style={{ fontSize: "1em", color: "#2e7d32", fontWeight: 500, textAlign: "right", minWidth: "110px" }}>
                Avg. {data.avgResponseDays != null ? `${data.avgResponseDays} days` : "N/A"}
              </span>
              <div style={{ display: "grid", gridTemplateColumns: "auto auto", columnGap: "4px", alignItems: "baseline", minWidth: "120px", justifyContent: "end" }}>
                <span style={{ fontSize: "1.4em", fontWeight: 700, color: "#3a4550", textAlign: "right" }}>
                  {data.responded.toLocaleString("en-GB")}
                </span>
                <span style={{ fontSize: "1.05em", color: "#2e7d32", fontWeight: 500, textAlign: "right", minWidth: "70px" }}>
                  ({pct(data.responded)}%)
                </span>
              </div>
            </div>

            {/* Per-source breakdown */}
            {data.sources && data.sources.length > 0 && (
              <div style={{ borderTop: "1px solid rgba(76, 175, 80, 0.25)", marginTop: "8px", paddingTop: "8px", display: "flex", flexDirection: "column", gap: "3px" }}>
                {data.sources.map((src) => {
                  const srcPct = src.total > 0 ? ((src.responded / src.total) * 100).toFixed(1) : "0.0";
                  return (
                    <div
                      key={src.name}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto auto",
                        alignItems: "center",
                        columnGap: "16px",
                        paddingLeft: "16px",
                        fontSize: "0.95em",
                      }}
                    >
                      <span style={{ color: "#2e7d32", fontWeight: 500 }}>{src.name}</span>
                      <span style={{ color: "#2e7d32", fontWeight: 500, textAlign: "right", minWidth: "110px" }}>
                        Avg. {src.avgResponseDays != null ? `${src.avgResponseDays} days` : "N/A"}
                      </span>
                      <div style={{ display: "grid", gridTemplateColumns: "auto auto", columnGap: "4px", alignItems: "baseline", minWidth: "120px", justifyContent: "end" }}>
                        <span style={{ fontWeight: 700, color: "#3a4550", textAlign: "right" }}>
                          {src.responded.toLocaleString("en-GB")}
                        </span>
                        <span style={{ color: "#2e7d32", fontWeight: 500, textAlign: "right", minWidth: "70px" }}>
                          ({srcPct}%)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Not Responded */}
          <div
            style={getRowStyle("notResponded", "rgba(245, 124, 0, 0.06)")}
            onClick={() => goToReviewFeed("null")}
            onMouseEnter={() => setHoveredRow("notResponded")}
            onMouseLeave={() => setHoveredRow(null)}
            title="View reviews not responded to"
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div style={{ width: "4px", height: "28px", borderRadius: "2px", backgroundColor: "#f57c00" }} />
              <div style={{ fontSize: "1.2em", fontWeight: 600, color: "#3a4550" }}>Not Responded</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <span style={{ fontSize: "1.5em", fontWeight: 700, color: "#3a4550" }}>{data.notResponded.toLocaleString("en-GB")}</span>
              <span style={{ fontSize: "1.15em", color: "#666", marginLeft: "6px" }}>({pct(data.notResponded)}%)</span>
            </div>
          </div>

          {/* Cannot Respond */}
          <div
            style={{...getRowStyle("cannotRespond", "rgba(158, 158, 158, 0.06)"), marginBottom: 0}}
            onClick={() => goToReviewFeed("NA")}
            onMouseEnter={() => setHoveredRow("cannotRespond")}
            onMouseLeave={() => setHoveredRow(null)}
            title="View reviews that cannot be responded to"
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div style={{ width: "4px", height: "28px", borderRadius: "2px", backgroundColor: "#9e9e9e" }} />
              <div style={{ fontSize: "1.2em", fontWeight: 600, color: "#3a4550" }}>Cannot Respond</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <span style={{ fontSize: "1.5em", fontWeight: 700, color: "#3a4550" }}>{data.cannotRespond.toLocaleString("en-GB")}</span>
              <span style={{ fontSize: "1.15em", color: "#666", marginLeft: "6px" }}>({pct(data.cannotRespond)}%)</span>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );

  const cardStyle: React.CSSProperties = {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  };

  return <div className="SummaryCard" style={cardStyle}>{content}</div>;
};

export default ResponseTrackerCard;
